import {
  Listbox,
  ListboxItem,
  ListboxSection,
  Tooltip,
} from "@nextui-org/react";
import { SectionHeader } from "app/components/sectionHeader";
import * as React from "react";
import { SHOP_TIMEZONES, useLocations } from "./useLocations";
import classNames from "classnames";
import { Collapse } from "react-collapse";
import PhoneInput from "react-phone-input-2";
import { ArrowPathIcon, TrashIcon } from "@heroicons/react/24/outline";
import { NewLocationModal } from "./components/newLocationModal";
import {
  AsBadge,
  AsButton,
  AsIconButton,
  AsInput,
  reactSelectStyles,
} from "app/subframe";
import { Icon } from "@subframe/core";
import { EmptyState } from "app/components/emptyState";
import { UpgradeBanner } from "app/billing/components/upgradeBanner";
import {Edit, Plus, Store, Trash2} from "lucide-react";

interface LocationsProps {}

export const Locations: React.FC<LocationsProps> = () => {
  const {
    validShops,
    locationStates,
    onLocationInputChange,
    onLocationSelect,
    isCreatingOrUpdatingNewShop,
    onSubmit,
    openShops,
    toggleShop,
    onShopArchiveToggle,
    isNewLocationModalOpen,
    setIsNewLocationModalOpen,
    setPhoneValue,
    paywallPermissions,
    handleInputChange,
    handleTimezoneChange,
  } = useLocations();

  return (
    <>
      <NewLocationModal
        isOpen={isNewLocationModalOpen}
        onOpenChange={setIsNewLocationModalOpen}
      />
      <div className="flex relative flex-col w-full gap-4 ">
        {validShops.map((shop) => {
          const locationState = locationStates[shop.id];
          const isShopIncomplete =
            shop.name === "" ||
            shop.email === "" ||
            shop.phone === "" ||
            !shop.addressLine1;
          const shopIsArchived = !!shop.archivedAt;


          return (
              <div key={shop.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Store className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">{shop.name || "New Shop"}
                        </h5>
                      <p className="text-sm text-gray-500">{shop.addressLine1}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">


                    {isShopIncomplete ? (
                        <AsBadge variant="warning" className="rounded">
                          Incomplete
                        </AsBadge>
                    ) :
                    shopIsArchived ? (
                        <AsBadge variant="neutral" className="rounded">
                          Archived
                        </AsBadge>
                    ): <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        !shopIsArchived
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                    }`}>
                                {shopIsArchived ? '' :'Active'}
                              </span>}



                    <button
                        onClick={() => toggleShop(shop.id)}
                        className="p-1 rounded text-gray-500 hover:text-gray-600 hover:bg-gray-200"
                    >
                      <Edit className="w-5 h-5" />
                    </button>

                    <AsIconButton
                        variant="ghost"
                        className="rounded-sm"
                        onClick={() => onShopArchiveToggle(shop, shopIsArchived)}
                        size="sm"
                        leadingIcon={
                          shopIsArchived ? "FeatherRotateCcw" : "FeatherTrash2"
                        }
                    >
                      {shopIsArchived ? (
                          <Tooltip content="Unarchive">
                            <ArrowPathIcon className="h-4 w-4 text-[#71717A]" />
                          </Tooltip>
                      ) : (
                          <Tooltip content="Archive">
                            <TrashIcon className="h-4 w-4 text-[#ffffff]" />
                            <Trash2 className="w-4 h-4" />
                          </Tooltip>
                      )}
                    </AsIconButton>

                    <div className="flex gap-2 items-center">

                      <Icon
                          name={
                            openShops[shop.id]
                                ? "FeatherChevronUp"
                                : "FeatherChevronDown"
                          }
                          className="w-4 h-4"
                      />
                    </div>

                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <span className="ml-2 text-gray-900">{shop.phone}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className="ml-2 text-gray-900">{shop.email}</span>
                  </div>
                </div>
                <Collapse isOpened={openShops[shop.id]}>
                  <div className="py-4 gap-10 flex flex-col">
                    <form
                        className="flex flex-col gap-6"
                        onSubmit={(e) => onSubmit(shop.id, e)}
                    >
                      {/* Location Name */}
                      <div className="flex flex-col gap-2">
                        <label className="text-[#71717A] text-sm">
                          Location name
                        </label>
                        <AsInput
                            name="name"
                            placeholder="West End"
                            value={locationState.formValues.name}
                            required
                            textPosition="left"
                            onChange={(e) =>
                                handleInputChange(shop.id, "name", e.target.value)
                            }
                        />
                      </div>

                      {/* Address Input with Suggestions */}
                      <div className="flex flex-col gap-2 relative">
                        <label className="text-[#71717A] text-sm">Address</label>
                        <AsInput
                            name="address"
                            placeholder="100 Main St, New York, NY 10001"
                            value={locationState.addressInputText}
                            onChange={(e) =>
                                onLocationInputChange(shop.id, e.target.value)
                            }
                            required
                            textPosition="left"
                        />
                        {locationState.locationSuggestions.length > 0 && (
                            <Listbox
                                className="absolute top-16 z-30 w-full"
                                classNames={{
                                  base: "!bg-white rounded shadow-sm",
                                }}
                                onAction={(key: any) =>
                                    onLocationSelect(shop.id, key)
                                }
                            >
                              <ListboxSection title="Suggestions" showDivider>
                                {locationState.locationSuggestions.map(
                                    (location) => (
                                        <ListboxItem
                                            classNames={{
                                              base: "hover:bg-[#E4E4E4] rounded-sm",
                                            }}
                                            key={location.name}
                                            textValue={location.name}
                                        >
                                          {location.name}
                                        </ListboxItem>
                                    )
                                )}
                              </ListboxSection>
                            </Listbox>
                        )}
                      </div>

                      {/* Contact Email */}
                      <div className="flex flex-col gap-2">
                        <label className="text-zinc-500 text-sm">
                          Contact email
                        </label>
                        <AsInput
                            name="email"
                            type="email"
                            placeholder="you@company.com"
                            defaultValue={shop.email}
                            required
                            textPosition="left"
                            onChange={(e) =>
                                handleInputChange(shop.id, "email", e.target.value)
                            }
                        />
                      </div>

                      {/* Contact Phone */}
                      <div className="flex flex-col gap-2">
                        <label className="text-zinc-500 text-sm">
                          Contact phone
                        </label>
                        <PhoneInput
                            country={"us"}
                            value={shop.phone}
                            specialLabel=""
                            onChange={(value) => setPhoneValue(shop.id, value)}
                            containerClass="phone-input-container"
                            inputStyle={{
                              padding: "10px",
                              width: "100%",
                              fontSize: "14px",
                              fontWeight: "600",
                              border: "1px solid #F4F4F5",
                              borderRadius: "4px",
                            }}
                            inputProps={{
                              name: "phone",
                              required: true,
                            }}
                        />
                      </div>

                      {/* Tax Percentage */}
                      <div className="flex flex-col gap-2">
                        <label className="text-zinc-500 text-sm">
                          Tax Percentage (%)
                        </label>
                        <AsInput
                            name="taxPercentage"
                            type="decimal"
                            placeholder="0.0"
                            defaultValue={shop.taxPercentage}
                            required
                            textPosition="left"
                            onChange={(e) =>
                                handleInputChange(
                                    shop.id,
                                    "taxPercentage",
                                    e.target.value
                                )
                            }
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <label className="text-zinc-500 text-sm">
                          Timezone
                        </label>
                        <select
                            name="timeZoneId"
                            value={locationState.formValues.timeZoneId}
                            onChange={(e) =>
                                handleTimezoneChange(shop.id, e.target.value)
                            }
                            className="w-full rounded border border-[#F4F4F5] bg-white px-3 py-2.5 text-sm font-semibold text-gray-900"
                        >
                          {SHOP_TIMEZONES.map((zone) => (
                              <option key={zone.id} value={zone.id}>
                                {zone.name}
                              </option>
                          ))}
                          {!SHOP_TIMEZONES.some(
                              (zone) =>
                                  zone.id === locationState.formValues.timeZoneId
                          ) &&
                            locationState.formValues.timeZoneId && (
                              <option value={locationState.formValues.timeZoneId}>
                                {locationState.formValues.timeZoneName ||
                                  locationState.formValues.timeZoneId}
                              </option>
                            )}
                        </select>
                      </div>

                      <AsButton
                          variant="neutral-secondary"
                          className="w-fit px-6 self-end"
                          type="submit"
                          loader={isCreatingOrUpdatingNewShop}
                          disabled={
                              isCreatingOrUpdatingNewShop || !locationState.isDirty
                          }
                          size="md"
                          text="Update"
                      />
                    </form>
                  </div>
                </Collapse>
              </div>
          )
        })}

        {/*loader={isCreatingOrUpdatingNewShop}*/}
        {/*<button*/}
        {/*    onClick={() => setIsNewLocationModalOpen(true)}*/}
        {/*    disabled={*/}
        {/*        isCreatingOrUpdatingNewShop*/}
        {/*    }*/}
        {/*    className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"*/}
        {/*>*/}
        {/*  <Plus className="w-6 h-6 mx-auto mb-2 text-gray-400" />*/}
        {/*  <span className="text-sm font-medium text-gray-600">Add New Location</span>*/}
        {/*</button>*/}
        {/*{!paywallPermissions?.addLocation && (*/}
        {/*  <UpgradeBanner text="Upgrade to add multiple locations." />*/}
        {/*)}*/}
        {validShops.length == 0 && (
          <div className="flex flex-col gap-4 justify-center items-center h-[75dvh]">
            <EmptyState />
            <AsButton
              text={"Add Location"}
              onClick={() => setIsNewLocationModalOpen(true)}
            />
          </div>
        )}
      </div>
    </>
  );
};
